/**
 * Body of the cell comment popover (OR-1534, Figma "Notes").
 *
 * Two shapes, both from the mockups:
 *  - no open thread → just the composer (avatar + "Add a comment" + send);
 *  - an open thread → "Comment" header with Resolve (check) + close (X), the
 *    comments oldest first in a scrolling list, and the composer pinned at
 *    the bottom so more can be added.
 *
 * Every comment shows its author and a timestamp (see `formatCommentTime`);
 * only the author gets the "..." menu (Edit / Delete) — the backend enforces
 * the same rule with a 403. Editing swaps the body for a textarea with
 * Cancel / Save in place.
 *
 * The panel talks to the API itself (thread, then its comments — the thread
 * only carries counts) and reports two things up: the cell's new
 * open-comment count (for the badge) and a resolved thread (the caller
 * closes the popover and raises the "Thread resolved" message with Undo).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { ArrowUp, CheckCircle, DotsHorizontal, Edit05, Trash01, X } from "@untitledui/icons";
import {
    Button as AriaButton,
    Menu as AriaMenu,
    MenuItem as AriaMenuItem,
    MenuTrigger as AriaMenuTrigger,
    Popover as AriaPopover,
} from "react-aria-components";
import { Avatar, Button, ButtonUtility, cx } from "@tricura/atlas-ui";
import {
    ApiError,
    MAX_COMMENT_LENGTH,
    deleteSubmissionComment,
    getOpenCommentThread,
    listCommentThreadComments,
    replyToCommentThread,
    setCommentThreadResolved,
    startCommentThread,
    updateSubmissionComment,
} from "../../api";
import { useAuth } from "../../auth";
import type { UserDisplay } from "../../types/submission";
import type { CommentPage, SubmissionCommentThreadWire, SubmissionCommentWire } from "../../types/submission-comment";
import { Skeleton } from "../skeleton";
import { formatCommentTime } from "./comment-time";

// ───────────────────────────── people ─────────────────────────────

interface Person {
    name: string;
    initials: string;
}

function personFrom(first: string | null | undefined, last: string | null | undefined, email: string | null | undefined): Person {
    const name = [first, last].filter(Boolean).join(" ") || email || "";
    const initials = `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || email?.[0]?.toUpperCase() || "?";
    return { name, initials };
}

function personOf(user: UserDisplay | null): Person {
    return personFrom(user?.first_name, user?.last_name, user?.email);
}

/** `/v2/user/me/` comes back snake_case even though `User` is typed
 *  camelCase (the type mirrors the old v1 shape) — read both. */
function useCurrentPerson(): { id: string | null; person: Person } {
    const { user } = useAuth();
    const raw = (user ?? {}) as Record<string, unknown>;
    const str = (k: string) => (typeof raw[k] === "string" ? (raw[k] as string) : null);
    return {
        id: user?.id != null ? String(user.id) : null,
        person: personFrom(str("first_name") ?? str("firstName"), str("last_name") ?? str("lastName"), str("email")),
    };
}

/** Re-render every minute so "X min ago" labels move on their own. */
function useMinuteClock(): number {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = window.setInterval(() => setNow(Date.now()), 60_000);
        return () => window.clearInterval(id);
    }, []);
    return now;
}

function errorMessage(err: unknown): string {
    if (err instanceof ApiError) return err.detail ?? err.message;
    return err instanceof Error ? err.message : String(err);
}

// ───────────────────────────── textarea ─────────────────────────────

/** Plain textarea that grows with its content (Figma "Typing"), up to a cap
 *  after which it scrolls. */
function AutoGrowTextarea({
    value,
    onChange,
    onKeyDown,
    placeholder,
    ariaLabel,
    autoFocus,
    className,
    "data-autotest": dataAutotest,
}: {
    value: string;
    onChange: (next: string) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
    placeholder?: string;
    ariaLabel: string;
    autoFocus?: boolean;
    className?: string;
    "data-autotest"?: string;
}) {
    const ref = useRef<HTMLTextAreaElement>(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
    }, [value]);
    useEffect(() => {
        if (!autoFocus) return;
        const el = ref.current;
        el?.focus();
        el?.setSelectionRange(el.value.length, el.value.length);
    }, [autoFocus]);
    return (
        <textarea
            ref={ref}
            rows={1}
            value={value}
            maxLength={MAX_COMMENT_LENGTH}
            placeholder={placeholder}
            aria-label={ariaLabel}
            data-autotest={dataAutotest}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            className={cx(
                "text-primary placeholder:text-placeholder w-full resize-none bg-transparent text-sm leading-5 outline-none",
                className,
            )}
        />
    );
}

/** Cmd/Ctrl+Enter submits; a bare Enter is a new line (comments are
 *  multi-paragraph in the mockups). */
function isSubmitShortcut(e: KeyboardEvent) {
    return e.key === "Enter" && (e.metaKey || e.ctrlKey);
}

// ───────────────────────────── composer ─────────────────────────────

function Composer({ me, onSubmit }: { me: Person; onSubmit: (body: string) => Promise<void> }) {
    const [draft, setDraft] = useState("");
    const [pending, setPending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const canSend = draft.trim().length > 0 && !pending;

    const send = async () => {
        if (!canSend) return;
        setPending(true);
        setError(null);
        try {
            await onSubmit(draft.trim());
            setDraft("");
        } catch (err) {
            // Keep the draft so nothing typed is lost.
            setError(errorMessage(err));
        } finally {
            setPending(false);
        }
    };

    return (
        <div className="flex shrink-0 flex-col gap-1.5 px-4 py-3" data-autotest="comment-composer">
            <div className="flex items-start gap-3">
                <Avatar size="sm" initials={me.initials} alt={me.name} />
                <div className="flex min-h-8 flex-1 items-center py-1.5">
                    <AutoGrowTextarea
                        value={draft}
                        onChange={setDraft}
                        placeholder="Add a comment"
                        ariaLabel="Add a comment"
                        autoFocus
                        data-autotest="comment-composer-input"
                        onKeyDown={(e) => {
                            if (isSubmitShortcut(e)) {
                                e.preventDefault();
                                void send();
                            }
                        }}
                    />
                </div>
                <AriaButton
                    aria-label="Send"
                    isDisabled={!canSend}
                    onPress={() => void send()}
                    data-autotest="comment-composer-send"
                    className="bg-brand-solid hover:bg-brand-solid_hover disabled:bg-brand-solid/40 mt-auto inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-white outline-none transition duration-100 ease-linear focus-visible:ring-2 focus-visible:ring-focus-ring disabled:cursor-not-allowed"
                >
                    <ArrowUp aria-hidden="true" className="size-4" />
                </AriaButton>
            </div>
            {error && (
                <p role="alert" className="text-error-primary pl-11 text-xs">
                    {error}
                </p>
            )}
        </div>
    );
}

// ───────────────────────────── one comment ─────────────────────────────

const MENU_ITEM_CLASS =
    "flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm font-semibold outline-none hover:bg-secondary_hover focus:bg-secondary_hover";

function CommentItem({
    comment,
    isMine,
    now,
    onSave,
    onDelete,
}: {
    comment: SubmissionCommentWire;
    isMine: boolean;
    now: number;
    onSave: (body: string) => Promise<void>;
    onDelete: () => Promise<void>;
}) {
    const author = personOf(comment.created_by_user);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(comment.body);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const canSave = draft.trim().length > 0 && draft.trim() !== comment.body && !pending;

    const run = async (action: () => Promise<void>) => {
        setPending(true);
        setError(null);
        try {
            await action();
            return true;
        } catch (err) {
            setError(errorMessage(err));
            return false;
        } finally {
            setPending(false);
        }
    };

    const save = async () => {
        if (!canSave) return;
        if (await run(() => onSave(draft.trim()))) setEditing(false);
    };

    return (
        <li className="flex gap-3" data-autotest="comment-item">
            <Avatar size="sm" initials={author.initials} alt={author.name} />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex min-h-8 items-center gap-2">
                    <span className="text-primary truncate text-sm font-semibold">{author.name}</span>
                    <time dateTime={comment.created_at} className="text-tertiary shrink-0 text-xs">
                        {formatCommentTime(comment.created_at, now)}
                    </time>
                    {isMine && !editing && (
                        <AriaMenuTrigger>
                            <AriaButton
                                aria-label="Comment actions"
                                data-autotest="comment-item-actions"
                                className="text-fg-quaternary hover:text-fg-secondary hover:bg-secondary_hover aria-expanded:ring-focus-ring ml-auto inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md outline-none transition duration-100 ease-linear focus-visible:ring-2 focus-visible:ring-focus-ring aria-expanded:ring-2"
                            >
                                <DotsHorizontal aria-hidden="true" className="size-4" />
                            </AriaButton>
                            <AriaPopover placement="bottom end" offset={4} className="z-60 entering:animate-in entering:fade-in entering:zoom-in-95">
                                <AriaMenu
                                    aria-label="Comment actions"
                                    className="bg-primary border-secondary w-40 rounded-lg border p-1 shadow-lg outline-none"
                                    onAction={(key) => {
                                        if (key === "edit") {
                                            setDraft(comment.body);
                                            setEditing(true);
                                        } else if (key === "delete") {
                                            void run(onDelete);
                                        }
                                    }}
                                >
                                    <AriaMenuItem id="edit" textValue="Edit" className={cx(MENU_ITEM_CLASS, "text-secondary")} data-autotest="comment-item-edit">
                                        <Edit05 aria-hidden="true" className="text-fg-quaternary size-4" />
                                        Edit
                                    </AriaMenuItem>
                                    <AriaMenuItem id="delete" textValue="Delete" className={cx(MENU_ITEM_CLASS, "text-error-primary")} data-autotest="comment-item-delete">
                                        <Trash01 aria-hidden="true" className="size-4" />
                                        Delete
                                    </AriaMenuItem>
                                </AriaMenu>
                            </AriaPopover>
                        </AriaMenuTrigger>
                    )}
                </div>

                {editing ? (
                    <div className="flex flex-col gap-3">
                        <div className="border-primary focus-within:ring-focus-ring rounded-lg border px-3 py-2 focus-within:ring-2">
                            <AutoGrowTextarea
                                value={draft}
                                onChange={setDraft}
                                ariaLabel="Edit comment"
                                autoFocus
                                data-autotest="comment-item-edit-input"
                                onKeyDown={(e) => {
                                    if (isSubmitShortcut(e)) {
                                        e.preventDefault();
                                        void save();
                                    } else if (e.key === "Escape") {
                                        // Leave edit mode without closing the whole popover.
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setEditing(false);
                                    }
                                }}
                            />
                        </div>
                        <div className="flex items-center justify-end gap-3">
                            <Button color="tertiary" size="sm" onClick={() => setEditing(false)} isDisabled={pending} data-autotest="comment-item-edit-cancel">
                                Cancel
                            </Button>
                            <Button color="primary" size="sm" onClick={() => void save()} isDisabled={!canSave} isLoading={pending} data-autotest="comment-item-edit-save">
                                Save
                            </Button>
                        </div>
                    </div>
                ) : (
                    <p className="text-secondary whitespace-pre-wrap break-words text-sm">{comment.body}</p>
                )}
                {error && (
                    <p role="alert" className="text-error-primary text-xs">
                        {error}
                    </p>
                )}
            </div>
        </li>
    );
}

// ───────────────────────────── panel ─────────────────────────────

export interface CommentThreadPanelProps {
    submissionId: string;
    page: CommentPage;
    field: string;
    /** Open-comment count the table knows about — decides whether to show a
     *  loading skeleton while the thread is fetched. */
    knownCount: number;
    onCountChange: (count: number) => void;
    onResolved: (thread: SubmissionCommentThreadWire) => void;
    onClose: () => void;
}

export function CommentThreadPanel({ submissionId, page, field, knownCount, onCountChange, onResolved, onClose }: CommentThreadPanelProps) {
    const me = useCurrentPerson();
    const now = useMinuteClock();
    const [thread, setThread] = useState<SubmissionCommentThreadWire | null>(null);
    const [comments, setCommentList] = useState<SubmissionCommentWire[]>([]);
    const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
    const [loadError, setLoadError] = useState<string | null>(null);
    const [resolving, setResolving] = useState(false);
    const [resolveError, setResolveError] = useState<string | null>(null);
    const listRef = useRef<HTMLUListElement>(null);
    // Callers pass fresh closures every render; reading them through a ref
    // keeps `load` stable so the fetch effect runs once per anchor.
    const onCountChangeRef = useRef(onCountChange);
    useLayoutEffect(() => {
        onCountChangeRef.current = onCountChange;
    });

    const load = useCallback(async () => {
        try {
            const t = await getOpenCommentThread(submissionId, page, field);
            const list = t ? await listCommentThreadComments(submissionId, t.id) : [];
            setThread(t);
            setCommentList(list);
            setStatus("ready");
            // Someone else may have commented or resolved since the list loaded.
            onCountChangeRef.current(list.length);
        } catch (err) {
            setLoadError(errorMessage(err));
            setStatus("error");
        }
    }, [submissionId, page, field]);

    useEffect(() => {
        void load();
    }, [load]);

    const setComments = (next: SubmissionCommentWire[]) => {
        setCommentList(next);
        setThread((prev) => (prev ? { ...prev, num_comments: next.length } : prev));
        onCountChange(next.length);
    };

    const post = async (body: string) => {
        if (thread) {
            try {
                const created = await replyToCommentThread(submissionId, thread.id, body);
                setComments([...comments, created]);
            } catch (err) {
                // 409: someone resolved the thread while this was being typed.
                // Re-read (the cell is empty again) and let the composer keep
                // the draft and show the backend's message, rather than
                // dropping the reply into a fresh thread with no context.
                if (err instanceof ApiError && err.status === 409) await load();
                throw err;
            }
        } else {
            // First comment on the cell: the thread comes back with counts
            // only, so re-read to get its id and the comment itself.
            await startCommentThread(submissionId, { page, field }, body);
            await load();
        }
        requestAnimationFrame(() => listRef.current?.lastElementChild?.scrollIntoView({ block: "nearest" }));
    };

    const resolve = async () => {
        if (!thread) return;
        setResolving(true);
        setResolveError(null);
        try {
            const resolved = await setCommentThreadResolved(submissionId, thread.id, true);
            onCountChange(0);
            onResolved(resolved);
        } catch (err) {
            setResolveError(errorMessage(err));
        } finally {
            setResolving(false);
        }
    };

    if (status === "loading" && knownCount > 0) {
        return (
            <div className="flex flex-col gap-4 p-4" aria-busy="true">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
            </div>
        );
    }

    if (status === "error") {
        return (
            <p role="alert" className="text-error-primary p-4 text-sm">
                {loadError}
            </p>
        );
    }

    const hasThread = comments.length > 0;

    return (
        <div className="flex max-h-[560px] min-h-0 flex-col">
            {hasThread && (
                <>
                    <div className="flex shrink-0 items-center gap-1 px-4 pt-4 pb-2">
                        <h3 className="text-primary flex-1 text-md font-semibold">Comment</h3>
                        <ButtonUtility
                            color="tertiary"
                            size="sm"
                            icon={CheckCircle}
                            tooltip="Resolve thread"
                            onClick={() => void resolve()}
                            isDisabled={resolving}
                            data-autotest="comment-thread-resolve"
                        />
                        <ButtonUtility color="tertiary" size="sm" icon={X} tooltip="Close" onClick={onClose} data-autotest="comment-thread-close" />
                    </div>
                    {resolveError && (
                        <p role="alert" className="text-error-primary px-4 text-xs">
                            {resolveError}
                        </p>
                    )}
                    <ul ref={listRef} className="atlas-scrollbar flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pt-2 pb-4">
                        {comments.map((c) => (
                            <CommentItem
                                key={c.id}
                                comment={c}
                                now={now}
                                isMine={me.id !== null && c.created_by !== null && String(c.created_by) === me.id}
                                onSave={async (body) => {
                                    const updated = await updateSubmissionComment(submissionId, c.id, body);
                                    setComments(comments.map((x) => (x.id === c.id ? updated : x)));
                                }}
                                onDelete={async () => {
                                    await deleteSubmissionComment(submissionId, c.id);
                                    setComments(comments.filter((x) => x.id !== c.id));
                                }}
                            />
                        ))}
                    </ul>
                    <hr aria-hidden="true" className="border-secondary shrink-0" />
                </>
            )}
            <Composer me={me.person} onSubmit={post} />
        </div>
    );
}
